import { useState, useEffect, useRef } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { debouncedLoadAdminUsers } from 'utils/optionsLoaders'
import { useNotification } from 'components/Notification'

import { fetchCustomerAccountManagers, upsertCustomerAccountManagers } from 'services/customers'

const DEFAULT_QUERY_PARAMS = {
  managerIds: [],
  managers: [],
  receivesAssistantNotifications: false,
}

const useCustomerAccountManager = ({ customer }) => {
  const [queryParams, setQueryParams] = useState(DEFAULT_QUERY_PARAMS)
  const [originalValues, setOriginalValues] = useState({})
  const loadUsers = term => debouncedLoadAdminUsers({ term })
  const { createNotification } = useNotification()
  const isInitialLoad = useRef(true)
  const queryClient = useQueryClient()

  const params = {
    managerIds: queryParams.managerIds,
    receivesAssistantNotifications: queryParams.receivesAssistantNotifications,
  }

  const handleManagerChange = params => {
    setQueryParams({ ...queryParams, managerIds: params.map(p => p.value), managers: params })
  }

  const handleReceiveNotificationChange = receivesAssistantNotifications => {
    setQueryParams({ ...queryParams, receivesAssistantNotifications })
  }

  const { data: accountManagers, isFetched } = useQuery({
    queryKey: ['useCustomerAccountManager', customer.id],
    queryFn: () => fetchCustomerAccountManagers(customer.id, params),
    initialData: [],
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const managers = accountManagers.map(am => am.manager)

    setQueryParams(prev => ({
      ...prev,
      managerIds: managers.map(m => m.id),
      managers: managers.map(m => ({
        id: m.id,
        value: m.id,
        label: `${m.firstName} ${m.lastName} (${m.email})`,
      })),
      // setting receives assistant notifications is an all or nothing operation
      // if 1 manager is opted in, they are all opted in
      // if 1 manager is opted out, they are all opted out
      receivesAssistantNotifications: accountManagers[0]?.receivesAssistantNotifications || false,
    }))

    // we store the original values on the first load so we can check for changes in state
    if (isInitialLoad.current && isFetched) {
      setOriginalValues({
        managerIds: managers.map(m => m.id),
        receivesAssistantNotifications: accountManagers[0]?.receivesAssistantNotifications,
      })
      isInitialLoad.current = false
    }
  }, [accountManagers, isFetched])

  // If there have not been any changes from the original values (ie: saved values)
  // then the `update` button is disabled.
  const disableUpdate = () => {
    if (isEmpty(accountManagers)) {
      return false
    }

    const managerChanged =
      JSON.stringify(queryParams.managerIds.sort()) !==
      JSON.stringify(originalValues.managerIds?.sort())

    const receiveNotificationChanged =
      queryParams.receivesAssistantNotifications !== originalValues.receivesAssistantNotifications

    return !(managerChanged || receiveNotificationChanged)
  }

  const {
    mutate: upsertCustomerAccountManagersMutation,
    isLoading: isUpsertingAccountManager,
  } = useMutation({
    mutationFn: params => upsertCustomerAccountManagers(customer.id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['useCustomerAccountManager'] })
      setOriginalValues({
        managerIds: queryParams.managerIds,
        receivesAssistantNotifications: queryParams.receivesAssistantNotifications,
      })
      createNotification({
        variant: 'success',
        message: 'Account manager updated successfully',
      })
    },
    onError: () => {
      createNotification({
        variant: 'error',
        message: 'Unable to update the account manager',
      })
    },
  })

  const handleSubmit = () => upsertCustomerAccountManagersMutation(params)

  return {
    queryParams,
    accountManagers,
    loadUsers,
    disableUpdate,
    handleManagerChange,
    handleReceiveNotificationChange,
    handleSubmit,
    isUpsertingAccountManager,
  }
}

export default useCustomerAccountManager
